import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import jsQR from "jsqr";
import { X, Smartphone, Copy, CheckCircle2, Clock, IndianRupee, ExternalLink, ScanLine, Phone, Camera } from "lucide-react";
import api, { formatINR } from "../lib/api";
import { buildUpiLink, isValidUpiId } from "../lib/upi";
import { toast } from "sonner";

export const EXPENSE_CATEGORIES = ["Fuel", "Toll", "Maintenance", "Salary", "Office", "Misc", "Rent", "Electricity", "Insurance", "EMI", "GST/Tax"];

// Reusable "Pay via UPI" modal. Pass a `prefill` object:
//   { payee_name, upi_id, amount, note, category, ref_type, ref_id, ref_number }
// On success it POSTs to /payments and calls onSaved(payment).
export function UpiPayModal({ open, prefill = {}, onClose, onSaved }) {
  const [f, setF] = useState(prefill);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [payees, setPayees] = useState([]);
  const [savePayee, setSavePayee] = useState(false);
  const [mode, setMode] = useState("expense");      // "lr" | "expense"
  const [lrQuery, setLrQuery] = useState("");
  const [lrResults, setLrResults] = useState([]);
  const [lrOpen, setLrOpen] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const lockedExisting = f.ref_type === "expense" && !!f.ref_id;   // paying an existing expense
  const lockedPersonal = f.ref_type === "personal";                 // personal advance/draw

  useEffect(() => {
    if (open) {
      const init = { payee_name: "", upi_id: "", phone: "", amount: "", note: "", category: "", ref_type: "other", ref_id: "", ref_number: "", ...prefill };
      setF(init);
      setSavePayee(false);
      setMode(init.ref_type === "lr" ? "lr" : "expense");
      setLrQuery(init.ref_type === "lr" ? (init.ref_number || "") : "");
      setLrResults([]); setLrOpen(false);
      api.get("/payees").then((r) => setPayees(r.data || [])).catch(() => setPayees([]));
    } else {
      stopCamera();
    }
  }, [open]); // eslint-disable-line
  useEffect(() => () => stopCamera(), []); // cleanup on unmount

  useEffect(() => {
    if (mode !== "lr" || !lrOpen) return;
    const t = setTimeout(() => {
      api.get("/lrs", { params: lrQuery ? { q: lrQuery } : {} })
        .then((r) => setLrResults((r.data || []).slice(0, 12)))
        .catch(() => setLrResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [lrQuery, mode, lrOpen]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const upiLink = useMemo(() => buildUpiLink(f), [f]);
  const upiOk = isValidUpiId(f.upi_id);

  function applyParsed(parsed) {
    setF((p) => ({
      ...p,
      upi_id: parsed.pa,
      payee_name: p.payee_name || decodeURIComponent(parsed.pn || ""),
      amount: p.amount || parsed.am || "",
      note: p.note || decodeURIComponent(parsed.tn || ""),
    }));
  }

  function stopCamera() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    const s = streamRef.current;
    if (s) { s.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setCameraOn(false);
  }

  function tickCamera() {
    const v = videoRef.current;
    if (!v || v.readyState < 2 || !v.videoWidth) { rafRef.current = requestAnimationFrame(tickCamera); return; }
    const w = v.videoWidth, h = v.videoHeight;
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    let code = null;
    try { const data = ctx.getImageData(0, 0, w, h); code = jsQR(data.data, w, h, { inversionAttempts: "dontInvert" }); } catch { /* ignore */ }
    if (code && code.data) {
      const parsed = parseUpiString(code.data);
      if (parsed && parsed.pa) { applyParsed(parsed); toast.success(`Scanned UPI ID: ${parsed.pa}`); stopCamera(); return; }
    }
    rafRef.current = requestAnimationFrame(tickCamera);
  }

  async function startCamera() {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Camera not supported on this device"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      streamRef.current = stream;
      setCameraOn(true);
      setTimeout(() => {
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        v.setAttribute("playsinline", "true");
        v.play().catch(() => {});
        rafRef.current = requestAnimationFrame(tickCamera);
      }, 60);
    } catch { toast.error("Couldn't open the camera — allow camera permission and try again"); }
  }

  function pickPayee(id) {
    const p = payees.find((x) => x.id === id);
    if (!p) return;
    setF((prev) => ({ ...prev, payee_name: p.name || prev.payee_name, upi_id: p.upi_id || "", phone: p.phone || "", category: p.category || prev.category }));
  }

  if (!open) return null;

  // Decode a UPI QR from a photo the payee sent (their scanner/QR image).
  function parseUpiString(text) {
    if (!text) return null;
    const t = text.trim();
    if (t.toLowerCase().startsWith("upi://") || t.includes("pa=")) {
      try {
        const qs = t.split("?")[1] || t;
        const params = new URLSearchParams(qs);
        return { pa: params.get("pa") || "", pn: params.get("pn") || "", am: params.get("am") || "", tn: params.get("tn") || "" };
      } catch { /* fall through */ }
    }
    // A bare VPA like name@bank
    if (/^[\w.\-]{2,}@[\w.\-]{2,}$/.test(t)) return { pa: t, pn: "", am: "", tn: "" };
    return null;
  }

  async function onScanFile(e) {
    const file = e.target.files?.[0];
    if (file) await scanImage(file);
    if (fileRef.current) fileRef.current.value = "";
  }

  function scanImage(file) {
    return new Promise((resolve) => {
      setScanning(true);
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const maxDim = 1400;
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale); height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        let code = null;
        try {
          const data = ctx.getImageData(0, 0, width, height);
          code = jsQR(data.data, width, height, { inversionAttempts: "attemptBoth" });
        } catch { /* ignore */ }
        URL.revokeObjectURL(url);
        setScanning(false);
        if (!code || !code.data) { toast.error("Couldn't read a QR in that photo — try a clearer, closer shot"); return resolve(); }
        const parsed = parseUpiString(code.data);
        if (!parsed || !parsed.pa) { toast.error("That QR isn't a UPI payment code"); return resolve(); }
        applyParsed(parsed);
        toast.success(`Scanned UPI ID: ${parsed.pa}`);
        resolve();
      };
      img.onerror = () => { URL.revokeObjectURL(url); setScanning(false); toast.error("Could not open that image"); resolve(); };
      img.src = url;
    });
  }

  async function record(status) {
    if (!(String(f.payee_name || "").trim() || String(f.upi_id || "").trim() || String(f.phone || "").trim())) {
      toast.error("Enter a payee name, UPI ID or phone"); return;
    }
    if (Number(f.amount || 0) <= 0) { toast.error("Enter an amount"); return; }
    // Resolve the "purpose" into the payment's ref fields (unless it's a locked link).
    let payload = { ...f, amount: Number(f.amount || 0), status, save_payee: savePayee };
    if (!lockedExisting && !lockedPersonal) {
      if (mode === "lr") {
        if (!f.ref_id) { toast.error("Pick the LR this payment is for"); return; }
        payload = { ...payload, ref_type: "lr" };
      } else {
        payload = { ...payload, ref_type: "expense", ref_id: "", ref_number: "", category: f.category || "Misc" };
      }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/payments", payload);
      toast.success(status === "paid" ? "Payment logged as Paid" : "Payment saved as Pending");
      onSaved && onSaved(data);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save payment");
    } finally { setSaving(false); }
  }

  const copyLink = () => { navigator.clipboard.writeText(upiLink); toast.success("UPI link copied"); };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-start justify-center overflow-y-auto py-8" data-testid="upi-pay-modal" onClick={onClose}>
      <div className="bg-[hsl(var(--surface))] text-[hsl(var(--text-main))] w-full max-w-lg mx-4 rounded-sm border border-[hsl(var(--border))]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-[#C84B31]" />
            <h2 className="font-headings text-lg font-bold">Pay via UPI</h2>
          </div>
          <button className="btn-ghost" onClick={onClose} data-testid="upi-modal-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* QR + open-app */}
          <div className="flex flex-col sm:flex-row gap-5 items-center">
            <div className="bg-white p-3 rounded-md border border-[hsl(var(--border))] shrink-0" data-testid="upi-qr">
              <QRCodeSVG value={upiLink} size={148} includeMargin={false} level="M" />
            </div>
            <div className="flex-1 w-full space-y-2">
              <p className="text-xs text-[hsl(var(--text-muted))]">Scan with any UPI app, or open it on this phone:</p>
              <a href={upiLink} className="btn-primary w-full justify-center" data-testid="upi-open-app">
                <Smartphone className="w-4 h-4" /> Open in UPI app
              </a>
              <button type="button" className="btn-secondary w-full justify-center" onClick={copyLink} data-testid="upi-copy-link">
                <Copy className="w-4 h-4" /> Copy pay link
              </button>
              <a href={upiLink} className="text-[11px] text-[#C84B31] hover:underline inline-flex items-center gap-1 break-all">
                <ExternalLink className="w-3 h-3 shrink-0" /> {f.upi_id || "no UPI id — name & amount only"}
              </a>
            </div>
          </div>

          {/* Scan the payee's QR / scanner photo */}
          <div className="rounded-md border border-dashed border-[hsl(var(--border))] p-3 flex items-center gap-3">
            <ScanLine className="w-5 h-5 text-[#C84B31] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">Got the payee's QR / scanner photo?</div>
              <div className="text-[11px] text-[hsl(var(--text-muted))]">Upload it and we'll auto-read their UPI ID.</div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <button type="button" className="btn-secondary" onClick={() => fileRef.current && fileRef.current.click()} disabled={scanning || cameraOn} data-testid="upi-scan-btn">
                {scanning ? "Scanning…" : "Scan photo"}
              </button>
              {cameraOn
                ? <button type="button" className="btn-ghost text-[#8A2526]" onClick={stopCamera} data-testid="upi-camera-stop">Stop camera</button>
                : <button type="button" className="btn-ghost inline-flex items-center gap-1" onClick={startCamera} data-testid="upi-camera-btn"><Camera className="w-4 h-4" /> Use camera</button>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onScanFile} data-testid="upi-scan-input" />
          </div>

          {cameraOn && (
            <div className="relative rounded-md overflow-hidden border border-[hsl(var(--border))] bg-black" data-testid="upi-camera-view">
              <video ref={videoRef} className="w-full max-h-72 object-contain" muted playsInline />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="w-40 h-40 border-2 border-[#C84B31] rounded-lg animate-pulse" />
              </div>
              <div className="absolute bottom-2 left-0 right-0 text-center text-[11px] text-white/90">Point at the payee's QR — it scans automatically</div>
            </div>
          )}

          {payees.length > 0 && (
            <div>
              <label className="input-label">Favourite payee (one-tap)</label>
              <select className="input-field" value="" onChange={(e) => pickPayee(e.target.value)} data-testid="upi-payee-picker">
                <option value="">— Pick a saved payee —</option>
                {payees.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.upi_id ? ` · ${p.upi_id}` : p.phone ? ` · ${p.phone}` : ""}</option>
                ))}
              </select>
            </div>
          )}

          {/* editable fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="input-label">Payee name</label>
              <input className="input-field" value={f.payee_name || ""} onChange={(e) => set("payee_name", e.target.value)} placeholder="Driver / broker / vendor" data-testid="upi-payee-name" />
            </div>
            <div>
              <label className="input-label">UPI ID (VPA)</label>
              <input className={`input-field ${!upiOk ? "border-[#DA1E28]" : ""}`} value={f.upi_id || ""} onChange={(e) => set("upi_id", e.target.value)} placeholder="name@bank" data-testid="upi-vpa" />
              {!upiOk && <p className="text-[11px] text-[#DA1E28] mt-1">Looks like an invalid UPI ID (expected name@bank).</p>}
            </div>
            <div>
              <label className="input-label">Phone number (if no UPI ID)</label>
              <input className="input-field" value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="10-digit mobile" data-testid="upi-phone" inputMode="tel" />
            </div>
            <div>
              <label className="input-label">Amount (₹)</label>
              <input type="number" step="0.01" className="input-field" value={f.amount ?? ""} onChange={(e) => set("amount", e.target.value)} data-testid="upi-amount" />
            </div>
          </div>
          <div>
            <label className="input-label">Note</label>
            <input className="input-field" value={f.note || ""} onChange={(e) => set("note", e.target.value)} placeholder="What is this payment for?" data-testid="upi-note" />
          </div>

          {/* Purpose: link to an LR, or book as a non-LR expense */}
          {lockedPersonal ? (
            <div className="rounded-md border border-[hsl(var(--border))] p-3 text-xs text-[hsl(var(--text-muted))]" data-testid="upi-purpose-personal">
              Recorded as a <span className="font-medium text-[hsl(var(--text-main))]">personal advance</span>{f.ref_number ? ` for ${f.ref_number}` : ""} — this won't be booked as a business expense.
            </div>
          ) : lockedExisting ? (
            <div className="rounded-md border border-[hsl(var(--border))] p-3 text-xs text-[hsl(var(--text-muted))]" data-testid="upi-purpose-existing">
              Settling existing expense <span className="font-medium text-[hsl(var(--text-main))]">{f.ref_number || ""}</span>.
            </div>
          ) : (
            <div data-testid="upi-purpose">
              <label className="input-label">What's this payment for?</label>
              <div className="flex gap-2 mb-2">
                <button type="button" onClick={() => setMode("lr")} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${mode === "lr" ? "border-[#C84B31] text-[#C84B31] bg-[#C84B31]/5" : "border-[hsl(var(--border))]"}`} data-testid="upi-purpose-lr">Link to an LR</button>
                <button type="button" onClick={() => setMode("expense")} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${mode === "expense" ? "border-[#C84B31] text-[#C84B31] bg-[#C84B31]/5" : "border-[hsl(var(--border))]"}`} data-testid="upi-purpose-expense">Non-LR expense</button>
              </div>

              {mode === "lr" ? (
                <div className="relative">
                  <input className="input-field" placeholder="Search LR by number or party…"
                    value={lrQuery}
                    onFocus={() => setLrOpen(true)}
                    onChange={(e) => { setLrQuery(e.target.value); setLrOpen(true); setF((p) => ({ ...p, ref_id: "", ref_number: "" })); }}
                    data-testid="upi-lr-search" />
                  {f.ref_id ? <div className="text-[11px] text-[#1f8a4c] mt-1" data-testid="upi-lr-selected">Linked to {f.ref_number} — its expense total will include this.</div> : null}
                  {lrOpen && lrResults.length > 0 && !f.ref_id && (
                    <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-lg">
                      {lrResults.map((lr) => (
                        <button key={lr.id} type="button" className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--text-main)/0.05)] text-sm" data-testid={`upi-lr-opt-${lr.id}`}
                          onClick={() => { setF((p) => ({ ...p, ref_id: lr.id, ref_number: lr.lr_number || "" })); setLrQuery(lr.lr_number || ""); setLrOpen(false); }}>
                          <span className="font-mono font-medium text-[#C84B31]">{lr.lr_number}</span>
                          <span className="text-[hsl(var(--text-muted))]"> · {lr.consignor_name} → {lr.consignee_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <select className="input-field" value={EXPENSE_CATEGORIES.includes(f.category) ? f.category : "Misc"} onChange={(e) => set("category", e.target.value)} data-testid="upi-expense-category">
                  {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c === "Salary" ? "Staff Salary" : c}</option>)}
                </select>
              )}
              <p className="text-[11px] text-[hsl(var(--text-muted))] mt-1.5">When you mark this Paid, it's auto-booked into Expenses{mode === "lr" ? " against this LR" : ""} for clean books.</p>
            </div>
          )}
          {f.phone && !String(f.upi_id || "").trim() ? (
            <div className="rounded-md border border-[#E9B949]/50 bg-[#E9B949]/10 p-3 flex items-start gap-3" data-testid="upi-phone-hint">
              <Phone className="w-4 h-4 text-[#8A6D1B] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 text-xs text-[hsl(var(--text-main))]">
                UPI can't auto-pay a raw phone number. Open your UPI app → <span className="font-medium">New payment → Pay phone number</span>, and paste <span className="font-mono font-medium">{f.phone}</span>.
              </div>
              <button type="button" className="btn-secondary shrink-0" onClick={() => { navigator.clipboard.writeText(String(f.phone)); toast.success("Number copied"); }} data-testid="upi-copy-phone">
                <Copy className="w-4 h-4" /> Copy number
              </button>
            </div>
          ) : null}
        </div>

        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex flex-wrap items-center justify-end gap-3">
          <label className="flex items-center gap-2 text-xs text-[hsl(var(--text-muted))] mr-auto cursor-pointer" data-testid="upi-save-payee-label">
            <input type="checkbox" checked={savePayee} onChange={(e) => setSavePayee(e.target.checked)} data-testid="upi-save-payee" />
            Save as favourite payee
          </label>
          <button className="btn-secondary" disabled={saving} onClick={() => record("pending")} data-testid="upi-save-pending">
            <Clock className="w-4 h-4" /> Save as Pending
          </button>
          <button className="btn-primary" disabled={saving} onClick={() => record("paid")} data-testid="upi-save-paid">
            <CheckCircle2 className="w-4 h-4" /> {saving ? "Saving…" : "Mark as Paid"}
          </button>
        </div>
      </div>
    </div>
  );
}
